import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  CacheableMethod,
  EdtellFormControl,
  EdtellFormGroup,
  LoginService
} from "@edtell-common";
import { cloneDeep } from "lodash";
import moment from "moment";
import { Observable } from 'rxjs';
import { first } from 'rxjs/operators';
import { apiURL } from 'src/environments/environment';
import { PaymentModuleDataInterface } from "../../customer/interfaces/payment-module-data.interface";
import { ProgramInterface } from "../../customer/interfaces/programs/program.interface";
import { falsey } from "../../edtell-common/edtell-utilities/functions/falsey.function";
import { AllApiParameters } from "../../external-utilities/interfaces/lms-api-calls.interface";
import { CartStatus } from "../enums/cart-status.enum";
import { OperationStatus } from "../enums/operation-status.enum";
import { PaymentMethod } from "../enums/payment-method.enum";
import { TransactionType } from "../enums/transaction-type.enum";
import { WelcomeEmailStatus } from "../enums/welcome-email-status.enum";
import { AddCourseAutocompleteResult } from "../interfaces/add-course-autocomplete-result.interface";
import { ApplyPricingAdjustmentsToAutocompleteRequest } from "../interfaces/apply-pricing-adjustments-to-auto-req.interface";
import { RegConfirmEmailRequest } from "../interfaces/reg-confirm-email-request.interface";
import { RegistrationContactMapping } from "../interfaces/registration-contact-mapping.interface";
import { RegistrationContact } from "../interfaces/registration-contact.interface";
import { RegistrationCustomerMarkup } from "../interfaces/registration-customer-markup.interface";
import { RegistrationDetail } from "../interfaces/registration-detail.interface";
import { RegistrationPaymentMapping } from "../interfaces/registration-payment-mapping.interface";
import { RegistrationPayment } from "../interfaces/registration-payment.interface";
import { RegistrationProfileCustomerData } from '../interfaces/registration-profile-customer-data.interface';
import { RegistrationProfileData } from "../interfaces/registration-profile.interface";
import { Registration } from '../interfaces/registration.interface';
import { StudentHistoryCourse } from '../interfaces/student-history.interface';

// Used by external-utilities module - provided globally
@Injectable({
  providedIn: "root",
})
export class RegistrationProfileService {
  private httpOptions: object;
  private textHttpOptions: object;

  private registrationProfileData: RegistrationProfileData;
  private customerData: RegistrationProfileCustomerData;

  constructor(
    private http: HttpClient,
    private loginService: LoginService,
  ) {
    this.httpOptions = loginService.httpOptions;
    this.textHttpOptions = loginService.textHttpOptions;
  }

  getRegistrationProfile(
    registrationId?: string,
  ): Observable<RegistrationProfileData> {
    return new Observable((obs) => {
      if (registrationId) {
        if (!this.registrationProfileData) {
          this.getRegistrationProfileApiCall(registrationId).subscribe(
            (profile) => {
              this.registrationProfileData = profile;
              obs.next(profile);
            },
          );
        } else {
          obs.next(this.registrationProfileData);
        }
      } else {
        this.registrationProfileData = this.emptyRegistrationProfile();
        this.updateRegistrationProfile(this.registrationProfileData).subscribe(profile => {
          this.registrationProfileData = profile;
          obs.next(this.registrationProfileData);
        });
      }
    });
  }

  getCustomerData(regId?: string): Observable<RegistrationProfileCustomerData> {
    return new Observable((obs) => {
      if(!this.customerData) {
        this.getCustomerDataApiCall(regId).subscribe(data => {
          this.customerData = data;
          obs.next(data);
        });
      } else {
        obs.next(this.customerData);
      }
    });
  }

  @CacheableMethod()
  getCustomerDataApiCall(regId: string): Observable<RegistrationProfileCustomerData> {
    return this.http.get<RegistrationProfileCustomerData>(`${apiURL}/registrations-app/registrations/profile/customer-info?regId=${regId}`, this.httpOptions)
  }

  @CacheableMethod()
  getRegistrationProfileApiCall(registrationId: string) {
    return this.http.get<RegistrationProfileData>(
      `${apiURL}/registrations-app/registrations/profile?regId=${registrationId}`,
      this.httpOptions,
    );
  }

  getRegistrationContactsByRegId(registrationId) {
    return this.http.get<RegistrationContact[]>(
      `${apiURL}/registrations-app/registrations/profile/contacts?regId=${registrationId}`,
      this.httpOptions,
    );
  }

  getPayments() {
    return this.registrationProfileData.payments;
  }

  getDropMappings() {
    return this.registrationProfileData.dropMappings;
  }

  getPaymentsByTransactionId(transactionId: number) {
    return this.registrationProfileData.payments.filter((payment) =>
      payment.mappings.find(
        (mapping) => mapping.regDetailTransId == transactionId,
      ),
    );
  }

  getDetails() {
    return this.registrationProfileData.registration.details;
  }

  updateRegistrationCustomer(customerId: number, regId?: number) {
    return new Observable((obs) => {
      if (regId == null) {
        regId = this.registrationProfileData.registration.id;
      }
      this.http.put<RegistrationProfileCustomerData>(`${apiURL}/registrations-app/registrations/profile/customer?regId=${regId}&customerId=${customerId}`, null, this.httpOptions).subscribe(customerData => {
        this.registrationProfileData.registration.customerId = customerData.id;
        this.customerData = customerData;
        obs.next(customerData);
      });
    });
  }

  @CacheableMethod()
  getProgramById(id) {
    // TODO maybe rename or adjust URL to additional reg-side copy of endpoint?
    return this.http.get<ProgramInterface>(
      `${apiURL}/customer-app/customer/program?id=${id}`,
      this.httpOptions,
    );
  }

  @CacheableMethod()
  getAllLiveProgramsByCustomerId(customerId) {
    // TODO maybe rename or adjust URL to additional reg-side copy of endpoint?
    return this.http.get<ProgramInterface[]>(
      `${apiURL}/customer-app/customer/program/live?customerId=${customerId}`,
      this.httpOptions,
    );
  }

  updateRegistrationOwner(registration: Registration) {
    return this.http.put<Registration>(
      `${apiURL}/registrations-app/registrations/owner`,
      registration,
      this.httpOptions,
    );
  }

  updateRegistrationProfile(registration: RegistrationProfileData) {
    return this.http.put<RegistrationProfileData>(
      `${apiURL}/registrations-app/registrations/profile`,
      registration,
      this.httpOptions,
    );
  }

  updateStudentCredentials(registration: RegistrationProfileData) {
    return this.http.put<RegistrationProfileData>(
      `${apiURL}/registrations-app/registrations/profile/student-credentials`,
      registration,
      this.httpOptions,
    );
  }

  deleteRegistration(id: number) {
    return this.http.delete<boolean>(
      `${apiURL}/registrations-app/registrations/profile/delete?id=${id}`,
      this.httpOptions,
    );
  }

  recycleRegistration(regId: number) {
    return this.http.put<boolean>(
      `${apiURL}/registrations-app/registrations/profile/recycle?regId=${regId}`,
      null,
      this.httpOptions,
    );
  }

  getRecycledRegistrations() {
    return this.http.get<boolean>(
      `${apiURL}/registrations-app/registrations/profile/recycled`,
      this.httpOptions,
    );
  }

  restoreRegistrationFromRecycle(regId) {
    return this.http.put<boolean>(
      `${apiURL}/registrations-app/registrations/profile/restore-from-recycle?regId=${regId}`,
      null,
      this.httpOptions,
    );
  }

  @CacheableMethod()
  getStudentHistory(
    firstName: string,
    lastName: string,
    studentEmail: string,
    ignoreRegId: number,
  ) {
    return this.http.post<StudentHistoryCourse[]>(
      `${apiURL}/registrations-app/registrations/profile/student-history`,
      { firstName, lastName, email: studentEmail, ignoreRegId },
      this.httpOptions,
    );
  }

  getAddCourseAutocompleteResult(searchString: string) {
    return this.http.get<AddCourseAutocompleteResult[]>(
      `${apiURL}/registrations-app/reg-detail/course/autocomplete?searchString=${searchString}`,
      this.httpOptions,
    );
  }

  applyPricingAdjustmentsToAutoCompleteResults(
    req: ApplyPricingAdjustmentsToAutocompleteRequest,
  ) {
    return this.http.put<AddCourseAutocompleteResult[]>(
      `${apiURL}/registrations-app/reg-detail/course/autocomplete/pricing/adjustments`,
      req,
      this.httpOptions,
    );
  }

  updateLmsUser(providerId: number, regId: string) {
    return this.http.post<any>(
      `${apiURL}/registrations-app/registrations/lms/user?providerId=${providerId}&regId=${regId}`,
      {},
      this.httpOptions,
    );
  }

  enrollLmsStudent(providerId: number, regId: string, courseNum: string) {
    return this.http.post<any>(
      `${apiURL}/registrations-app/registrations/lms/user/enroll?providerId=${providerId}&regId=${regId}&courseNum=${courseNum}`,
      {},
      this.httpOptions,
    );
  }

  changeLmsStudentRole(params: AllApiParameters) {
    return this.http.post<any>(
      `${apiURL}/registrations-app/registrations/lms/user/role`,
      params,
      this.httpOptions,
    );
  }

  updateNcaaInfo(registration: Registration) {
    return this.http.put<Registration>(
      `${apiURL}/registrations-app/registrations/profile/ncaa`,
      registration,
      this.httpOptions,
    );
  }

  updateIepInfo(registration: Registration) {
    return this.http.put<Registration>(
      `${apiURL}/registrations-app/registrations/profile/iep`,
      registration,
      this.httpOptions,
    );
  }

  updateFncInfo(registration: Registration) {
    return this.http.put<Registration>(
      `${apiURL}/registrations-app/registrations/profile/fnc`,
      registration,
      this.httpOptions,
    );
  }

  sendRegConfirmEmail(req: RegConfirmEmailRequest) {
    return this.http.post<boolean>(
      `${apiURL}/registrations-app/registrations/reg-confirm/send`,
      req,
      this.httpOptions,
    );
  }

  getMarkupCourseTypePattern(markup: RegistrationCustomerMarkup) {
    return `${markup.rigorCode ?? "([A-Z]+)"}${markup.formatCode ?? "([A-Z]+)"}${markup.structureCode ?? "([A-Z]+)"}${markup.creditCode ?? "([A-Z]+)"}${markup.termCode ?? "([A-Z]+)"}`;
  }

  getCostToSchoolStudent(detail: RegistrationDetail) {
    return detail.licenseValue <= 0
      ? detail.catalogMSRP + detail.pricingOverrideAdjustment
      : 0;
  }

  getInitialCostToStudent(detail: RegistrationDetail) {
    return detail.licenseValue > 0 && this.licensesForDetailHaveSeats(detail)
      ? 0
      : this.getCostToSchoolStudent(detail) + detail.markupAdjustment;
  }

  getTotalInitialCost(detail: RegistrationDetail) {
    return (
      this.getInitialCostToStudent(detail) +
      this.getExtensionFees(detail) +
      this.getAdjustments(detail) +
      this.getMaterials(detail)
    );
  }

  getAdmin(detail: RegistrationDetail) {
    return detail.transactions.reduce((acc, curr) => acc + curr.adminAmount, 0);
  }

  getAdminWithoutDrop(detail: RegistrationDetail) {
    return detail.transactions
      .filter((t) => t.transType != TransactionType.DROP)
      .reduce((acc, curr) => acc + curr.adminAmount, 0);
  }

  getAdminDrop(detail: RegistrationDetail) {
    return detail.transactions
      .filter((t) => t.transType == TransactionType.DROP)
      .reduce((acc, curr) => acc + curr.adminAmount, 0);
  }

  getExtensionFees(detail: RegistrationDetail, ignoreLicenses = true) {
    return detail.transactions
      .filter(
        (t) =>
          t.transType == TransactionType.EXTEND &&
          (ignoreLicenses ? !this.transactionIsLicensed(t.id) : true),
      )
      .reduce((acc, curr) => acc + curr.saleAmount, 0);
  }

  getAdjustments(detail: RegistrationDetail, ignoreLicenses = true) {
    return detail.transactions
      .filter(
        (t) =>
          t.transType == TransactionType.ADJUST &&
          (ignoreLicenses ? !this.transactionIsLicensed(t.id) : true),
      )
      .reduce((acc, curr) => acc + curr.saleAmount, 0);
  }

  getAdminWithNegativeAdjustments(detail: RegistrationDetail) {
    return detail.transactions
      .filter((t) => t.transType == TransactionType.ADJUST && t.saleAmount < 0)
      .reduce((acc, curr) => acc + curr.adminAmount, 0);
  }

  getMaterials(detail: RegistrationDetail, ignoreLicenses = true) {
    return detail.transactions
      .filter(
        (t) =>
          t.transType == TransactionType.MATERIALS &&
          (ignoreLicenses ? !this.transactionIsLicensed(t.id) : true),
      )
      .reduce((acc, curr) => acc + curr.saleAmount, 0);
  }

  getDropped(detail: RegistrationDetail) {
    return detail.transactions
      .filter((t) => t.transType == TransactionType.DROP)
      .reduce((acc, curr) => acc + curr.saleAmount, 0);
  }

  getTax(detail: RegistrationDetail) {
    const allMappingsForDetail: RegistrationDetail[] =
      this.registrationProfileData.payments.reduce(
        (acc, curr) => [
          ...acc,
          ...curr.mappings.filter(
            (mapping) =>
              mapping.regDetailId == detail.id && +mapping.saleAmount != 0,
          ),
        ],
        [],
      );

    return allMappingsForDetail.reduce((acc, curr) => acc + curr.saleTax, 0);
  }

  getCourseTotalNoTax(detail: RegistrationDetail) {
    return (
      this.getInitialCostToStudent(detail) +
      this.getAdmin(detail) +
      this.getExtensionFees(detail) +
      this.getAdjustments(detail) +
      this.getMaterials(detail) -
      this.getDropped(detail)
    );
  }

  getCourseTotalWithTax(detail: RegistrationDetail) {
    return this.getCourseTotalNoTax(detail) + this.getTax(detail);
  }

  getCourseRegTotal(detail: RegistrationDetail) {
    return (
      (detail.licenseValue > 0
        ? detail.operationStatus == OperationStatus.DROPPED
          ? 0
          : detail.licenseValue
        : this.getInitialCostToStudent(detail)) +
      this.getExtensionFees(detail, false) +
      this.getAdjustments(detail, false) +
      this.getMaterials(detail, false) +
      this.getTax(detail) +
      this.getAdmin(detail) -
      this.getDropped(detail)
    );
  }

  async isRegistrationContactActive(regId: number | string) {
    const registrationProfileData: RegistrationProfileData = await this.getRegistrationProfile(regId.toString()).pipe(first()).toPromise();
    const customerData: RegistrationProfileCustomerData = await this.getCustomerData(regId.toString()).pipe(first()).toPromise();
    const currentContact = customerData.contacts.find((c) => c.id == registrationProfileData.registration.schoolContactId);
    return currentContact?.active ?? true;
  }

  private transactionIsLicensed(transactionId: number) {
    const paymentsForTransaction: RegistrationPayment[] =
      this.registrationProfileData.payments.filter((payment) =>
        payment.mappings.find(
          (mapping) => mapping.regDetailTransId == transactionId,
        ),
      );

    return paymentsForTransaction.find(
      (payment) =>
        payment.paymentMethod == PaymentMethod.PAYMENT_CODE &&
        this.registrationProfileData.paymentCodeInfo.hasOwnProperty(
          payment.paymentCode.toLowerCase(),
        ) &&
        this.registrationProfileData.paymentCodeInfo[
          payment.paymentCode.toLowerCase()
        ].hasLicense,
    );
  }

  usingLicensePaymentCode(paymentCode) {
    return this.registrationProfileData.paymentCodeInfo[
      paymentCode.toLowerCase()
    ].hasLicense;
  }

  public emptyRegistrationProfileCustomerData(): RegistrationProfileCustomerData {
    return {
      id: null,
      schoolCode: null,
      customerName: null,
      address1: null,
      address2: null,
      city: null,
      state: null,
      zip: null,
      country: null,
      county: null,
      proctoredExams: null,
      serviceRepName: null,
      salesRepName: null,
      specialSchool: null,
      specialSchoolText: null,
      summerEndDate: null,
      fax: null,
      contacts: [],
      markups: [],
      paymentCodeInfo: new Map(),
    };
  }

  private emptyRegistrationProfile(): RegistrationProfileData {
    return {
      registration: {
        id: null,
        programId: null,
        customerId: null,
        schoolContactId: null,
        cartStatus: CartStatus.SHOPPING,
        firstName: null,
        middleInitial: null,
        lastName: null,
        birthdate: null,
        gender: null,
        gradeLevel: null,
        expectedGradeLevel: null,
        email: null,
        mobilePhone: null,
        mobileCallsOptOut: false,
        mobileTextsOptOut: false,
        homePhone: null,
        homeCallsOptOut: false,
        address1: null,
        address2: null,
        city: null,
        state: null,
        zip: null,
        country: null,
        fnc: false,
        fncInfo: null,
        iep: false,
        iepText: null,
        ncaaProspect: false,
        ncaaStudentNumber: null,
        shipSameAsHome: false,
        shipToName: null,
        shipToAddress1: null,
        shipToAddress2: null,
        shipToCity: null,
        shipToState: null,
        shipToZip: null,
        shipToCountry: null,
        batchEnrollmentId: null,
        initiatedFrom: "manual",
        userAgent: null,
        batchId: null,
        dateModified: null,
        details: [],
      },
      contacts: [this.emptyRegistrationContact(0)],
      payments: [],
      dropMappings: [],
      paymentCodeInfo: new Map(),
    };
  }

  public emptyRegistrationContact(index: number): RegistrationContact {
    return {
      id: null,
      email: null,
      timeZone: null,
      mappings: [this.emptyRegistrationContactMapping(index)],
    };
  }

  public emptyRegistrationContactMapping(index: number): RegistrationContactMapping {
    return {
      id: null,
      regId: null,
      regDetailId: null,
      regContactId: null,
      contactRelationshipType: null,
      firstName: null,
      middleInitial: null,
      lastName: null,
      mobilePhone: null,
      homePhone: null,
      workPhone: null,
      progressEmailOptOut: false,
      homeProgressCallOptOut: false,
      mobileProgressCallOptOut: false,
      mobileProgressTextOptOut: false,
      workProgressCallOptOut: false,
      sortOrder: index
    };
  }

  /* FOR PAYMENTS */

  private paymentModule: PaymentModuleDataInterface;

  private program: ProgramInterface;

  setPaymentModule(paymentModule: PaymentModuleDataInterface) {
    this.paymentModule = paymentModule;
  }

  getPaymentModule() {
    return this.paymentModule;
  }

  setProgram(program: ProgramInterface) {
    this.program = program;
  }

  getProgram() {
    return this.program;
  }

  licensesForDetailHaveSeats(detail: RegistrationDetail) {
    const paymentsPayingForDetailWithLicensedPaymentCode: RegistrationPayment[] =
      this.registrationProfileData.payments.filter(
        (payment) =>
          payment.paymentMethod == PaymentMethod.PAYMENT_CODE &&
          this.registrationProfileData.paymentCodeInfo.hasOwnProperty(
            payment.paymentCode.toLowerCase(),
          ) &&
          this.registrationProfileData.paymentCodeInfo[
            payment.paymentCode.toLowerCase()
          ].hasLicense &&
          payment.mappings.find((mapping) => mapping.regDetailId == detail.id),
      );

    const paymentsPayingForDetailWithLicensesNotOccupied: RegistrationPayment[] =
      paymentsPayingForDetailWithLicensedPaymentCode.filter(
        (payment) => !this.isLicenseSeatOccupiedByReg(payment),
      );

    return (
      paymentsPayingForDetailWithLicensesNotOccupied.length <= 0 ||
      (paymentsPayingForDetailWithLicensesNotOccupied.length > 0 &&
        falsey(
          paymentsPayingForDetailWithLicensesNotOccupied.find(
            (payment) =>
              this.registrationProfileData.paymentCodeInfo[
                payment.paymentCode.toLowerCase()
              ].availableLicenseSeats <= 0,
          ),
        ))
    );
  }

  anyUsedLicenseEndDatesBeforeDetailEndDates(detail: RegistrationDetail) {
    const paymentsPayingForDetailWithLicensedPaymentCode: RegistrationPayment[] =
      this.registrationProfileData.payments.filter(
        (payment) =>
          payment.paymentMethod == PaymentMethod.PAYMENT_CODE &&
          this.registrationProfileData.paymentCodeInfo.hasOwnProperty(
            payment.paymentCode.toLowerCase(),
          ) &&
          this.registrationProfileData.paymentCodeInfo[
            payment.paymentCode.toLowerCase()
          ].hasLicense &&
          payment.mappings.find((mapping) => mapping.regDetailId == detail.id),
      );

    const usedPaymentCodeArray = [];
    for (const l of paymentsPayingForDetailWithLicensedPaymentCode) {
      usedPaymentCodeArray.push(l.paymentCode.toLowerCase());
    }

    const licensesUsedWithEndDateIssues = Object.keys(
      this.registrationProfileData.paymentCodeInfo,
    )
      .map((key) => this.registrationProfileData.paymentCodeInfo[key])
      .filter(
        (licenseUsed) =>
          licenseUsed.endDate < detail.endDate &&
          usedPaymentCodeArray.includes(licenseUsed.paymentCode.toLowerCase()),
      );

    if (licensesUsedWithEndDateIssues.length > 0) {
      return true;
    }
    return false;
  }

  anyUsedLicenseEndDatesBeforeDetailEndDatesResumeExtend(
    detail: RegistrationDetail,
    date: string,
  ) {
    const paymentsPayingForDetailWithLicensedPaymentCode: RegistrationPayment[] =
      this.registrationProfileData.payments.filter(
        (payment) =>
          payment.paymentMethod == PaymentMethod.PAYMENT_CODE &&
          this.registrationProfileData.paymentCodeInfo.hasOwnProperty(
            payment.paymentCode.toLowerCase(),
          ) &&
          this.registrationProfileData.paymentCodeInfo[
            payment.paymentCode.toLowerCase()
          ].hasLicense &&
          payment.mappings.find((mapping) => mapping.regDetailId == detail.id),
      );

    const usedPaymentCodeArray = [];
    for (const l of paymentsPayingForDetailWithLicensedPaymentCode) {
      usedPaymentCodeArray.push(l.paymentCode.toLowerCase());
    }

    const licensesUsedWithEndDateIssues = Object.keys(
      this.registrationProfileData.paymentCodeInfo,
    )
      .map((key) => this.registrationProfileData.paymentCodeInfo[key])
      .filter(
        (licenseUsed) =>
          licenseUsed.endDate < date &&
          usedPaymentCodeArray.includes(licenseUsed.paymentCode.toLowerCase()),
      );

    if (licensesUsedWithEndDateIssues.length > 0) {
      return true;
    }
    return false;
  }

  isLicenseSeatOccupiedByReg(payment: RegistrationPayment) {
    const allMappingsForPaymentCode: RegistrationPaymentMapping[] =
      this.registrationProfileData.payments
        .filter(
          (p) =>
            p.paymentMethod == PaymentMethod.PAYMENT_CODE &&
            p.paymentCode == payment.paymentCode,
        )
        .reduce((acc, curr) => acc.concat(curr.mappings), []);

    return !falsey(
      this.registrationProfileData.registration.details.find((detail) => {
        return (
          detail.weStatus != WelcomeEmailStatus.WE_HOLD &&
          (detail.operationStatus == OperationStatus.ACTIVE ||
            detail.operationStatus == OperationStatus.HOLD) &&
          falsey(detail.finalizationStatus) &&
          falsey(detail.finalGradeSent) &&
          moment(detail.endDate).isAfter(
            moment(Date.now()).subtract(5, "days"),
          ) &&
          !falsey(
            allMappingsForPaymentCode.find(
              (mapping) => mapping.regDetailId == detail.id,
            ),
          )
        );
      }),
    );
  }

  /* HELPER FUNCTIONS */

  // The method of determing manual reg creation is subject to change
  isManualRegCreation(): boolean {
    return this.registrationProfileData.registration.initiatedFrom == "manual";
  }

  scrubWeStatusFromRegProfileDetails(
    formGroup: EdtellFormGroup,
  ): RegistrationProfileData {
    // Some wackiness to make sure that welcome email statuses aren't being overriden when changed in other parts of the application
    // unless a change is explicitly made
    const detailsToSend: RegistrationDetail[] = cloneDeep(
      this.registrationProfileData.registration.details,
    );
    const detailKeys: string[] = Object.keys(formGroup.controls).filter(
      (key: string) => key.match(/detail[0-9]+/),
    );
    for (const key of detailKeys) {
      const detailFormGroup: EdtellFormGroup = formGroup.get(
        key,
      ) as EdtellFormGroup;
      const weStatusControl: EdtellFormControl = detailFormGroup.get(
        "weStatus",
      ) as EdtellFormControl;
      if (weStatusControl.isOriginal) {
        const detailIndex: number = +key.replace(/\D/g, "");
        if (
          typeof detailIndex == "number" &&
          !falsey(this.registrationProfileData.registration.details) &&
          this.registrationProfileData.registration.details.length - 1 >=
            detailIndex
        ) {
          detailsToSend[detailIndex].weStatus = null;
        }
      }
    }

    return {
      ...this.registrationProfileData,
      registration: {
        ...this.registrationProfileData.registration,
        details: [...detailsToSend],
      },
    };
  }

  getPaymentOptions() {
    const paymentOptions = [];

    if (this.paymentModule == null) {
      return paymentOptions;
    }

    let enforcePaymentCode = false;
    if (this.program) {
      enforcePaymentCode = this.program.enforcePaymentCode;
    }

    if (this.paymentModule.acceptPaymentCode) {
      paymentOptions.push({
        text: this.paymentModule.paymentCodeLanguage,
        value: PaymentMethod.PAYMENT_CODE,
      });
    }

    if (this.paymentModule.acceptCredit && !enforcePaymentCode) {
      paymentOptions.push({
        text: this.paymentModule.creditLanguage,
        value: PaymentMethod.CREDIT_CARD,
      });
    }

    if (this.paymentModule.acceptCheck && !enforcePaymentCode) {
      paymentOptions.push({
        text: this.paymentModule.checkLanguage,
        value: PaymentMethod.CHECK_OR_MONEY_ORDER,
      });
    }

    return paymentOptions;
  }
}
